import { ScenarioHandler, BaseScenario, ScenarioResult, AssistantContext } from '../types';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';

/**
 * Document Scenario Handler
 *
 * Handelt vier soorten document/foto queries:
 * 1. Foto's tonen: "show me de voorgevel van @EMIE-12"
 * 2. Documenten opvragen: "Geef me het laatste EPC van @EMIE-12"
 * 3. Document geldigheid controleren: "Is er een geldig keuringsattest voor @HOOG-45"
 * 4. Document analyse: "Analyseer document 180" of "Wat is het label van dit EPC?"
 */
export class DocumentScenario implements ScenarioHandler {
  examples = [
    'Show me de voorgevel van de @EMIE-12',
    'Hoe ziet de keuken van de EMIE-12-V2 eruit?',
    'Geef me foto\'s van de badkamer van @HOOG-45-001',
    'Geef me het laatste EPC van de EMIE-12-2',
    'Is er een geldig keuringsattest voor de elektriciteitsinstallatie van de HOOG-45?',
    'Toon me het huurcontract van @EMIE-12-001',
    'Welke documenten zijn er van @HOOG-45?',
    'Is het EPC van @EMIE-12 nog geldig?',
    'Analyseer document 180',
    'Wat is het label van dit EPC?',
    'Geef me een samenvatting van document 180',
  ];

  detect(input: string): BaseScenario {
    const normalized = input.toLowerCase();

    // Document analysis keywords - asking to analyze a specific document by ID
    const analysisKeywords = [
      'analyseer', 'analyze', 'wat staat er in', 'lees', 'geef een samenvatting',
      'samenvatting van', 'wat is het label', 'wat zegt', 'bekijk document'
    ];
    const hasAnalysisKeyword = analysisKeywords.some(kw => normalized.includes(kw));

    // Check for document ID pattern: "document 180", "doc 180", "ID 180"
    const documentIdMatch = normalized.match(/(?:document|doc|id)\s*(\d+)/);

    // High confidence document analysis if we have analysis keyword + document ID
    if (hasAnalysisKeyword && documentIdMatch) {
      return { type: 'document-analysis', confidence: 0.95 };
    }

    // Picture query keywords - asking to see/show images
    const pictureKeywords = [
      'show me', 'toon', 'laat zien', 'foto', 'foto\'s', 'afbeelding', 'afbeeldingen',
      'hoe ziet', 'zie', 'beeld', 'beelden', 'picture', 'pictures', 'image'
    ];
    const hasPictureKeyword = pictureKeywords.some(kw => normalized.includes(kw));

    // Document query keywords - asking for documents
    const documentKeywords = [
      'geef me', 'laatste', 'document', 'documenten', 'contract', 'attest',
      'certificaat', 'vergunning', 'epc', 'rapport', 'factuur', 'bewijs'
    ];
    const hasDocumentKeyword = documentKeywords.some(kw => normalized.includes(kw));

    // Validity check keywords - asking if document is valid/exists
    const validityKeywords = [
      'is er een', 'geldig', 'geldige', 'verlopen', 'verlopen?', 'bestaat er',
      'hebben we', 'nog geldig', 'up to date', 'actueel'
    ];
    const hasValidityKeyword = validityKeywords.some(kw => normalized.includes(kw));

    // Document type keywords (labels) - common document/picture categories
    const labelKeywords = [
      'voorgevel', 'achtergevel', 'zijgevel', 'gevel',
      'keuken', 'badkamer', 'toilet', 'living', 'slaapkamer', 'tuin',
      'epc', 'energieprestatiecertificaat',
      'keuringsattest', 'keuring', 'elektriciteit', 'elektriciteitsinstallatie',
      'gas', 'gasinstallatie',
      'huurcontract', 'contract', 'lease',
      'verzekering', 'polis',
      'brandverzekering', 'aansprakelijkheid',
      'verhuis', 'plaatsbeschrijving',
      'factuur', 'rekening',
      'plan', 'tekening', 'blauwdruk'
    ];
    const hasLabelKeyword = labelKeywords.some(kw => normalized.includes(kw));

    // Entity mention keywords - mentions building or unit
    const hasEntityMention = /@[\w-]+/.test(input) ||
                            /\d{4}-[A-Z]+-\d+/i.test(input) ||
                            /[A-Z]+-\d+/i.test(input);

    // Picture queries with high confidence
    if (hasPictureKeyword && hasLabelKeyword && hasEntityMention) {
      return { type: 'document-picture', confidence: 0.95 };
    }

    // Validity checks with high confidence
    if (hasValidityKeyword && hasLabelKeyword && hasEntityMention) {
      return { type: 'document-validity', confidence: 0.95 };
    }

    // Document queries with high confidence
    if (hasDocumentKeyword && hasLabelKeyword && hasEntityMention) {
      return { type: 'document-query', confidence: 0.9 };
    }

    // Medium confidence for picture queries without entity
    if (hasPictureKeyword && hasLabelKeyword) {
      return { type: 'document-picture', confidence: 0.7 };
    }

    // Medium confidence for document queries without entity
    if (hasDocumentKeyword && hasLabelKeyword) {
      return { type: 'document-query', confidence: 0.7 };
    }

    // Lower confidence for validity checks without entity
    if (hasValidityKeyword && hasLabelKeyword) {
      return { type: 'document-validity', confidence: 0.65 };
    }

    return { type: 'unknown', confidence: 0 };
  }

  async parse(input: string, context: AssistantContext): Promise<ScenarioResult> {
    // Check for document analysis first (doesn't need OpenAI parsing)
    const detection = this.detect(input);
    if (detection.type === 'document-analysis') {
      return this.parseDocumentAnalysis(input);
    }

    // Use OpenAI to parse the query for better natural language understanding
    try {
      const parseResponse = await fetchWithAuth(
        `${context.apiUrl}/file/ai/parse-query`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: input }),
        },
        context.session
      );

      if (parseResponse.ok) {
        const aiParsed = await parseResponse.json();

        console.log('🤖 OpenAI parsed query:', aiParsed);

        // Use AI-parsed data if confidence is high enough
        if (aiParsed.confidence >= 0.6) {
          return this.convertAIParsedToScenarioResult(aiParsed);
        }
      }
    } catch (error) {
      console.warn('Failed to parse with OpenAI, falling back to keyword matching:', error);
    }

    // Fallback to keyword-based detection
    if (detection.type === 'document-picture') {
      return this.parsePictureQuery(input);
    } else if (detection.type === 'document-query') {
      return this.parseDocumentQuery(input);
    } else if (detection.type === 'document-validity') {
      return this.parseValidityCheck(input);
    }

    throw new Error('Onbekend document scenario');
  }

  private parseDocumentAnalysis(input: string): ScenarioResult {
    // Extract document ID from query
    const normalized = input.toLowerCase();
    const documentIdMatch = normalized.match(/(?:document|doc|id)\s*(\d+)/);

    if (!documentIdMatch) {
      throw new Error('Geen document ID gevonden in de vraag');
    }

    const documentId = parseInt(documentIdMatch[1], 10);

    // Extract the actual question (everything after the document ID mention)
    let question = input;
    const questionMatch = input.match(/(?:document|doc|id)\s*\d+\s*(.+)/i);
    if (questionMatch && questionMatch[1].trim()) {
      question = questionMatch[1].trim();
    } else {
      // Default question if none provided
      question = 'Wat is het label van dit document?';
    }

    return {
      scenario: 'document-analysis',
      action: 'analyze',
      data: {
        documentId,
        question,
      },
      preview: {
        type: 'analysis',
        documentId,
        question,
      }
    };
  }

  private convertAIParsedToScenarioResult(aiParsed: any): ScenarioResult {
    const intentMap = {
      'picture': 'document-picture',
      'document': 'document-query',
      'validity': 'document-validity',
    };

    const scenario = intentMap[aiParsed.intent as keyof typeof intentMap] || 'document-query';

    return {
      scenario: scenario as any,
      action: 'query',
      data: {
        label: aiParsed.label,
        buildingId: aiParsed.buildingId,
        unitId: aiParsed.unitId,
        latestOnly: aiParsed.latestOnly || false,
      },
      preview: {
        type: aiParsed.intent,
        label: aiParsed.label,
        buildingId: aiParsed.buildingId,
        unitId: aiParsed.unitId,
        latestOnly: aiParsed.latestOnly,
      }
    };
  }

  private parsePictureQuery(input: string): ScenarioResult {
    // Extract unit ID first: @EMIE-12-V2 or EMIE-12-001 or 2000-NATI-144-GV
    let unitId: string | null = null;
    const fullUnitMatch = input.match(/@?(\d{4}-)?([A-Z]+-\d+-[\w-]+)\b/i);
    if (fullUnitMatch) {
      unitId = fullUnitMatch[2].toUpperCase();
      if (fullUnitMatch[1]) {
        unitId = fullUnitMatch[1] + unitId;
      }
    }

    // Extract building ID: @EMIE-12 or EMIE-12 or 2000-EMIE-12 or NATI-144
    let buildingId: string | null = null;
    const buildingMatch = input.match(/@?(\d{4}-)?([A-Z]+-\d+)\b/i);
    if (buildingMatch && !unitId) {
      // Only use building match if we didn't find a unit ID
      buildingId = buildingMatch[2].toUpperCase();
      if (buildingMatch[1]) {
        buildingId = buildingMatch[1] + buildingMatch[2].toUpperCase();
      }
    }

    // Extract label from common keywords
    const label = this.extractLabel(input);

    return {
      scenario: 'document-picture',
      action: 'query',
      data: {
        label,
        buildingId,
        unitId,
      },
      preview: {
        type: 'picture',
        label,
        buildingId,
        unitId,
      }
    };
  }

  private parseDocumentQuery(input: string): ScenarioResult {
    // Extract unit ID first: @EMIE-12-V2 or EMIE-12-001 or 2000-NATI-144-GV
    let unitId: string | null = null;
    const fullUnitMatch = input.match(/@?(\d{4}-)?([A-Z]+-\d+-[\w-]+)\b/i);
    if (fullUnitMatch) {
      unitId = fullUnitMatch[2].toUpperCase();
      if (fullUnitMatch[1]) {
        unitId = fullUnitMatch[1] + unitId;
      }
    }

    // Extract building ID: @EMIE-12 or EMIE-12 or 2000-EMIE-12 or NATI-144
    let buildingId: string | null = null;
    const buildingMatch = input.match(/@?(\d{4}-)?([A-Z]+-\d+)\b/i);
    if (buildingMatch && !unitId) {
      // Only use building match if we didn't find a unit ID
      buildingId = buildingMatch[2].toUpperCase();
      if (buildingMatch[1]) {
        buildingId = buildingMatch[1] + buildingMatch[2].toUpperCase();
      }
    }

    // Check if user wants "laatste" (latest) document
    const latestOnly = input.toLowerCase().includes('laatste') ||
                       input.toLowerCase().includes('meest recente');

    const label = this.extractLabel(input);

    return {
      scenario: 'document-query',
      action: 'query',
      data: {
        label,
        buildingId,
        unitId,
        latestOnly,
      },
      preview: {
        type: 'document',
        label,
        buildingId,
        unitId,
        latestOnly,
      }
    };
  }

  private parseValidityCheck(input: string): ScenarioResult {
    // Extract unit ID first: @EMIE-12-V2 or EMIE-12-001 or 2000-NATI-144-GV
    let unitId: string | null = null;
    const fullUnitMatch = input.match(/@?(\d{4}-)?([A-Z]+-\d+-[\w-]+)\b/i);
    if (fullUnitMatch) {
      unitId = fullUnitMatch[2].toUpperCase();
      if (fullUnitMatch[1]) {
        unitId = fullUnitMatch[1] + unitId;
      }
    }

    // Extract building ID: @EMIE-12 or EMIE-12 or 2000-EMIE-12 or NATI-144
    let buildingId: string | null = null;
    const buildingMatch = input.match(/@?(\d{4}-)?([A-Z]+-\d+)\b/i);
    if (buildingMatch && !unitId) {
      // Only use building match if we didn't find a unit ID
      buildingId = buildingMatch[2].toUpperCase();
      if (buildingMatch[1]) {
        buildingId = buildingMatch[1] + buildingMatch[2].toUpperCase();
      }
    }

    const label = this.extractLabel(input);

    return {
      scenario: 'document-validity',
      action: 'query',
      data: {
        label,
        buildingId,
        unitId,
      },
      preview: {
        type: 'validity',
        label,
        buildingId,
        unitId,
      }
    };
  }

  private extractLabel(input: string): string {
    const normalized = input.toLowerCase();

    // Map of Dutch document/picture labels to search terms
    const labelMappings: Record<string, string[]> = {
      'Voorgevel': ['voorgevel', 'voor gevel', 'front'],
      'Achtergevel': ['achtergevel', 'achter gevel', 'back'],
      'Zijgevel': ['zijgevel', 'zij gevel', 'side'],
      'Gevel': ['gevel'],
      'Keuken': ['keuken', 'kitchen'],
      'Badkamer': ['badkamer', 'bad', 'bathroom'],
      'Toilet': ['toilet', 'wc'],
      'Living': ['living', 'woonkamer', 'salon'],
      'Slaapkamer': ['slaapkamer', 'bedroom'],
      'Tuin': ['tuin', 'garden'],
      'EPC': ['epc', 'energieprestatiecertificaat', 'energie'],
      'Keuringsattest elektriciteit': ['keuringsattest elektriciteit', 'keuring elektriciteit', 'elektriciteitskeur', 'elektriciteitsinstallatie'],
      'Keuringsattest gas': ['keuringsattest gas', 'keuring gas', 'gasinstallatie'],
      'Huurcontract': ['huurcontract', 'contract', 'lease', 'huurovereenkomst'],
      'Verzekering': ['verzekering', 'polis', 'insurance'],
      'Brandverzekering': ['brandverzekering', 'brand verzekering'],
      'Plaatsbeschrijving': ['plaatsbeschrijving', 'verhuis', 'intrek', 'uittrek'],
      'Factuur': ['factuur', 'rekening', 'invoice'],
      'Plan': ['plan', 'tekening', 'blauwdruk'],
    };

    // Find best matching label
    for (const [label, keywords] of Object.entries(labelMappings)) {
      if (keywords.some(kw => normalized.includes(kw))) {
        return label;
      }
    }

    // Default to extracted term or "document"
    return 'document';
  }

  async execute(result: ScenarioResult, context: AssistantContext): Promise<any> {
    if (result.scenario === 'document-picture') {
      return this.executePictureQuery(result, context);
    } else if (result.scenario === 'document-query') {
      return this.executeDocumentQuery(result, context);
    } else if (result.scenario === 'document-validity') {
      return this.executeValidityCheck(result, context);
    } else if (result.scenario === 'document-analysis') {
      return this.executeDocumentAnalysis(result, context);
    }

    throw new Error('Unknown document action');
  }

  private async executeDocumentAnalysis(result: ScenarioResult, context: AssistantContext): Promise<any> {
    const { documentId, question } = result.data;

    console.log('📄 Analyzing document:', { documentId, question });

    const response = await fetchWithAuth(
      `${context.apiUrl}/file/ai/analyze-document/${documentId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          sessionId: `ella-doc-analysis-${Date.now()}`
        }),
      },
      context.session
    );

    if (!response.ok) {
      throw new Error('Fout bij analyseren van document');
    }

    const analysisResult = await response.json();

    // Return as text response for inline chat display
    return {
      type: 'text-response',
      message: analysisResult.analysis,
    };
  }

  private async executePictureQuery(result: ScenarioResult, context: AssistantContext): Promise<any> {
    const { label, buildingId, unitId } = result.data;

    console.log('🖼️ Fetching pictures:', { label, buildingId, unitId });

    const response = await fetchWithAuth(
      `${context.apiUrl}/file/ai/find-pictures`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, buildingId, unitId }),
      },
      context.session
    );

    if (!response.ok) {
      throw new Error('Fout bij ophalen van afbeeldingen');
    }

    return await response.json();
  }

  private async executeDocumentQuery(result: ScenarioResult, context: AssistantContext): Promise<any> {
    const { label, buildingId, unitId, latestOnly } = result.data;

    console.log('📄 Fetching documents:', { label, buildingId, unitId, latestOnly });

    const response = await fetchWithAuth(
      `${context.apiUrl}/file/ai/find-documents`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, buildingId, unitId, latestOnly }),
      },
      context.session
    );

    if (!response.ok) {
      throw new Error('Fout bij ophalen van documenten');
    }

    return await response.json();
  }

  private async executeValidityCheck(result: ScenarioResult, context: AssistantContext): Promise<any> {
    const { label, buildingId, unitId } = result.data;

    console.log('✅ Checking document validity:', { label, buildingId, unitId });

    const response = await fetchWithAuth(
      `${context.apiUrl}/file/ai/check-validity`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, buildingId, unitId }),
      },
      context.session
    );

    if (!response.ok) {
      throw new Error('Fout bij controleren van document geldigheid');
    }

    return await response.json();
  }
}
